/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The always-visible status row: horizontally-scrollable status chips that drive
 * `filter.status` (`全部` clears it), an edge mask fading the trailing chips into
 * a pinned filter-toggle button that shows/hides the collapsible filter panel.
 */

import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { kLeadStatusTabs } from '@euler/app/flows/leads/pages/list/functions/leadListConstants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SlidersHorizontal } from 'lucide-react-native';
import { memo } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

export const StatusFilterRow = memo(
  ({
    value,
    onChange,
    filtersExpanded,
    onToggleFilters,
  }: {
    value: string;
    onChange: (key: string) => void;
    filtersExpanded: boolean;
    onToggleFilters: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      showLeadingMask,
      showTrailingMask,
      onLayout,
      onContentSizeChange,
      onScroll,
    } = useScrollableMask({ orientation: 'horizontal' });

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: rpx(12),
        }}
      >
        <ListMask
          style={{ flex: 1 }}
          orientation="horizontal"
          location={0.06}
          leadingMask={showLeadingMask}
          trailingMask={showTrailingMask}
        >
          <ScrollView
            horizontal
            keyboardShouldPersistTaps="handled"
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={16}
            onLayout={onLayout}
            onContentSizeChange={onContentSizeChange}
            onScroll={onScroll}
            contentContainerStyle={{
              alignItems: 'center',
              paddingLeft: rpx(16),
              paddingRight: rpx(8),
              gap: rpx(4),
            }}
          >
            {kLeadStatusTabs.map(tab => {
              const active = tab.key === value;
              return (
                <TouchableOpacity
                  key={tab.key}
                  activeOpacity={0.7}
                  onPress={() => onChange(tab.key)}
                  style={{
                    height: rpx(34),
                    justifyContent: 'center',
                    paddingHorizontal: rpx(16),
                    borderRadius: rpx(999),
                    backgroundColor: active ? LD.brand : LD.surface,
                    borderWidth: 1,
                    borderColor: active ? LD.brand : LD.hairline,
                  }}
                >
                  <Typography.Text
                    size={rpx(13)}
                    color={active ? LD.surface : LD.steel}
                    bold
                  >
                    {tab.label}
                  </Typography.Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </ListMask>
        {/* Toggle pinned past the mask so the trailing chips fade into it. */}
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={onToggleFilters}
          style={{
            width: rpx(34),
            height: rpx(34),
            borderRadius: rpx(17),
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: rpx(4),
            marginRight: rpx(16),
          }}
        >
          <SlidersHorizontal
            size={rpx(18)}
            color={filtersExpanded ? LD.brand : LD.steel}
          />
        </TouchableOpacity>
      </View>
    );
  },
);
